import { NodeTypes } from '@/components/question-editor/editor-extensions/nodes/nodes';
import { MarkTypes } from './../editor-extensions/marks/marks';
import isArray from '@/utils/common/is-array';
import isObject from '@/utils/common/is-object';
import { JSONContent } from '@tiptap/core';
import { QuestionLanguage } from '@/resource/subject/subject.model';
import { axios } from '@/utils/axios';
import { AnswerSpaceType } from '../editor-extensions/nodes/node-answer-space';

const answerTypes = [MarkTypes.Answer, NodeTypes.AnswerSection];
const langTypes = [NodeTypes.LangEnglish, NodeTypes.LangMalay];
const listTypes = [
  NodeTypes.BulletList,
  NodeTypes.RomanList,
  NodeTypes.OrderedList,
];

async function generateMath(mathText: string): Promise<any> {
  const { data } = await axios.post('questions/math', { mathText });
  return data;
}

const answerJson: JSONContent = {
  type: NodeTypes.Doc,
  content: [] as JSONContent[],
};

const awaitMathJsons = {
  mathMarks: [] as JSONContent[],
  parentNodes: [] as JSONContent[],
};

const listTemplate = (type: string | undefined) => {
  return {
    type: type || NodeTypes.OrderedList,
    attrs: { start: 1 },
    content: [],
  };
};

const answerMarkTemplate = (text: string) => {
  return {
    content: [
      {
        type: NodeTypes.Paragraph,
        content: [{ type: NodeTypes.Text, text: text }],
      },
    ],
  };
};

const findAnswer = (json: JSONContent) => {
  let hasAnswer = json.type && answerTypes.includes(json.type);

  if (!hasAnswer && json.content && isArray(json.content)) {
    for (const content of json.content) {
      hasAnswer = findAnswer(content);

      if (hasAnswer) {
        break;
      }
    }
  }

  return hasAnswer;
};

const addAnswer = (json: JSONContent, answerList: JSONContent | null) => {
  if (!json.content) return;
  if (!answerJson.content) return;

  if (answerList && answerList.content) {
    answerList.content.push(...json.content);
  } else {
    answerJson.content.push(...json.content);
  }
};

const modiftyAnswerSection = (
  json: JSONContent,
  answerList: JSONContent | null,
) => {
  if (!json.content) return;

  addAnswer(json, answerList);

  json.content = [];

  if (json.attrs) {
    const id = json.attrs['id'] || null;
    const answerElement = document.getElementById(id);

    if (answerElement) {
      const spaceType = answerElement.getAttribute(
        'spacetype',
      ) as AnswerSpaceType;

      if (spaceType && spaceType === AnswerSpaceType.Line) {
        const lines =
          answerElement.offsetHeight &&
          Math.ceil(answerElement.offsetHeight / 30);

        json.content = [
          {
            type: NodeTypes.AnswerSpace,
            attrs: { lines, type: AnswerSpaceType.Line },
            content: [],
          },
        ];
      } else {
        json.attrs['style'] = `height: ${answerElement.offsetHeight}px;`;
      }
    } else {
      json.attrs['style'] = `height: 100px;`;
    }
  }
};

const modifyTable = (json: JSONContent, answerList: JSONContent | null) => {
  if (!json.content) return;

  const hasAnswer = findAnswer(json);

  if (hasAnswer) {
    const duplicateJson: JSONContent = JSON.parse(JSON.stringify(json));
    addAnswer({ content: [duplicateJson] }, answerList);
  }
};

const modifyLanguage = (json: JSONContent, languages: QuestionLanguage[]) => {
  if (!json.content) return;

  const language =
    json.type &&
    json.type.split('lang') &&
    json.type.split('lang')[1] &&
    (json.type.split('lang')[1].toLowerCase() as QuestionLanguage);

  if (language) {
    const isLangNotAvail = !languages.includes(language);

    if (isLangNotAvail) {
      json.content = [];
    }
  }
};

const modifyAnswerMark = (
  parentNode: JSONContent,
  answerList: JSONContent | null,
) => {
  if (parentNode.text) {
    addAnswer(answerMarkTemplate(parentNode.text), answerList);
    // NOTE: replace text with dummy text
    parentNode.text = 'A'.repeat(parentNode.text.length + 3);
  }
};

const modifyMathMark = (mark: JSONContent, parentNode: JSONContent) => {
  if (parentNode.text) {
    awaitMathJsons.parentNodes.push(parentNode);
    awaitMathJsons.mathMarks.push(mark);
  }
};

const modifyLangMark = (
  mark: JSONContent,
  languages: QuestionLanguage[],
  grantParentNode: JSONContent,
) => {
  if (mark.attrs && mark.attrs.language) {
    const currentLang = mark.attrs.language as QuestionLanguage;

    const isLangAvail = languages.find(language => language === currentLang);

    if (!isLangAvail) {
      mark.attrs['hidden'] = true;

      if (grantParentNode) {
        if (grantParentNode.attrs) {
          grantParentNode.attrs['style'] = 'display: none;';
        } else {
          grantParentNode['attrs'] = {};
          grantParentNode.attrs['style'] = 'display: none;';
        }
      }
    }
  }
};

const modifyMark = (
  mark: JSONContent,
  parentNode: JSONContent,
  grantParentNode: JSONContent,
  skipAnswerExtract: boolean,
  answerList: JSONContent | null,
  languages: QuestionLanguage[],
) => {
  const type = mark.type || '';
  const isAnswer = answerTypes.includes(type);
  const isMath = type === MarkTypes.math;
  const isLang = type === MarkTypes.language;

  if (isAnswer && !skipAnswerExtract) {
    modifyAnswerMark(parentNode, answerList);
  } else if (isMath) {
    modifyMathMark(mark, parentNode);
  } else if (isLang) {
    modifyLangMark(mark, languages, grantParentNode);
  }
};

// NOTE: Modify json by mutation
const modifyJson = (
  json: JSONContent,
  skipAnswerExtract: boolean,
  languages: QuestionLanguage[],
  answerList: JSONContent | null,
  parentJson: JSONContent,
) => {
  if (!isObject(json)) return;
  if (!answerJson.content) return;

  // NOTE: Handle NODE
  if (json.content) {
    const type = json.type || '';
    const isList = listTypes.includes(type);
    const isAnswer = answerTypes.includes(type);
    const isTable = type === NodeTypes.Table;
    const isListItem = type === NodeTypes.ListItem;
    const isLanguage = langTypes.includes(type);

    if (isList) {
      const newAnswerList = listTemplate(json.type);
      if (answerList) {
        answerList.content && answerList.content.push(newAnswerList);
      } else {
        answerJson.content.push(newAnswerList);
      }
      json.content.map(content =>
        modifyJson(content, skipAnswerExtract, languages, newAnswerList, json),
      );
    } else if (isListItem && answerList) {
      const emptyListItem: JSONContent = {
        type: NodeTypes.ListItem,
        content: [],
      };

      answerList.content?.push(emptyListItem);
      json.content.map(content =>
        modifyJson(content, skipAnswerExtract, languages, emptyListItem, json),
      );
    } else if (isAnswer && !skipAnswerExtract) {
      modiftyAnswerSection(json, answerList);
    } else if (isTable) {
      modifyTable(json, answerList);
      // NOTE: Pass fake answerList so that table's answer(s) not added to answerJson
      json.content.map(content =>
        modifyJson(
          content,
          skipAnswerExtract,
          languages,
          { content: [] },
          json,
        ),
      );
    } else if (isLanguage) {
      modifyLanguage(json, languages);
    } else if (isArray(json.content)) {
      //NOTE: default modification
      json.content.map(content =>
        modifyJson(content, skipAnswerExtract, languages, answerList, json),
      );
    }
  }

  //NOTE: handle Mark
  if (json.marks) {
    json.marks.map(mark =>
      modifyMark(
        mark,
        json,
        parentJson,
        skipAnswerExtract,
        answerList,
        languages,
      ),
    );
  }
};

const resolveAwaitMathJsons = async () => {
  const resolvedMarks = awaitMathJsons.parentNodes.map(
    async (parentNode, index) => {
      if (parentNode.text) {
        const mathText = await generateMath(parentNode.text);
        parentNode.text = ' ';
        const mathMark = awaitMathJsons.mathMarks[index];

        if (mathMark && mathMark.attrs) {
          mathMark.attrs.mathjax = mathText;
        }
      }
    },
  );

  await Promise.all(resolvedMarks);
};

export interface ModifyJson {
  languages: QuestionLanguage[];
  newJson: JSONContent;
  answerJson: JSONContent;
}

const getModifiedJson = async (
  json: JSONContent,
  languages: QuestionLanguage[],
  skipAnswerExtract: boolean,
) => {
  const modifiedJsons: ModifyJson[] = [];
  answerJson.content = [];

  if (languages.length > 1) {
    for (const language of languages) {
      const duplicateJson: JSONContent = JSON.parse(JSON.stringify(json));
      answerJson.content = [];
      awaitMathJsons.mathMarks = [];
      awaitMathJsons.parentNodes = [];

      modifyJson(duplicateJson, skipAnswerExtract, [language], null, json);
      modifyJson(answerJson, true, [language], { content: [] }, json);
      await resolveAwaitMathJsons();

      modifiedJsons.push({
        languages: [language],
        newJson: duplicateJson,
        answerJson: JSON.parse(JSON.stringify(answerJson)),
      });
    }
  }

  answerJson.content = [];
  awaitMathJsons.mathMarks = [];
  awaitMathJsons.parentNodes = [];

  const allLangJson: JSONContent = JSON.parse(JSON.stringify(json));

  modifyJson(allLangJson, skipAnswerExtract, languages, null, json);
  modifyJson(answerJson, true, languages, { content: [] }, json);
  await resolveAwaitMathJsons();

  modifiedJsons.push({
    languages: languages,
    newJson: allLangJson,
    answerJson,
  });

  return modifiedJsons;
};

export default getModifiedJson;
